import React from 'react';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { getSession } from 'src/services/session/store';

import { errorsToObject } from 'src/lib/error';
import { getEnvironment, getProdEnvironment } from 'src/lib/featureFlags';

const isProd = process.env.NODE_ENV === 'production'
  && getEnvironment().integrationServiceBaseUrl === getProdEnvironment().integrationServiceBaseUrl;

const { accountDomain } = getSession() || {};
let currentPagerDutyAccount = {
  accountDomain: accountDomain || null
};
let jiraMetadata = {
  type: null,
  version: null,
};

const keysToRedact = [
  'access_token',
  'state',
  'pd_html_url',
  'pd_displayname',
  'pd_avatar_url',
  'jira_displayname',
  'jira_html_url',
  'jira_avatar_url',
  'jwt',
  'code',
  'code_verifier',
];

const redactUrl = (url) => {
  if (typeof url !== 'string') return url;
  return keysToRedact.reduce((str, val) => {
    return str.replace(new RegExp(`${val}=[^&]*`), `${val}=[REDACTED]`);
  }, url);
};

Bugsnag.start({
  apiKey: '00fd80852044d5aaae22056257e2b656',
  plugins: [new BugsnagPluginReact(React)],
  onError: event => {
    event.addMetadata('pagerduty account', currentPagerDutyAccount);
    event.addMetadata('jira', jiraMetadata);

    event.breadcrumbs.forEach(b => {
      if (b.metadata && typeof b.metadata.request === 'string') {
        b.metadata.request = redactUrl(b.metadata.request);
      }
      if (b.metadata && b.type === 'navigation' && typeof b.metadata.from === 'string') {
        b.metadata.from = redactUrl(b.metadata.from);
      }
      if (b.metadata && b.type === 'navigation' && typeof b.metadata.to === 'string') {
        b.metadata.to = redactUrl(b.metadata.to);
      }
    });

    event.request.url = redactUrl(event.request.url);

    if (isProd) return true;

    console.log('Bugsnag error (is not reported to bugsnag in dev):\n', event);
    return false;
  }
});

const isBlackListed = (error) => {
  const messages = [];
  if (typeof error === 'string') messages.push(error);
  if (error && typeof error.name === 'string') messages.push(error.name);
  if (error && typeof error.message === 'string') messages.push(error.message);

  // Don't report common errors that are most
  // likely caused by connectivity issues or Jira access permissions.
  // Still print them to the console to simplify bugs investigation in case of support request
  return [
    'Please use this UI only in the context of Jira iframe',
    'This page works only in Jira iframe',
    'Can not connect to Jira',
    'Can not login to Jira',
    'Can not load Jira settings',
    'Invalid empty response from Jira, status = 0',
    'Invalid empty response from Jira, status = 401',
    'NetworkError when attempting to fetch resource',
    'Failed to fetch',
    'Not authorized. Your account is expired and cannot use the API',
    'Priorities are not enabled for your account',
    'Invalid JQL statement.',
    'field is required',
    'Errors by field:',
    'CSSStyleSheet',
    'BetterJsPop',
    /^cancelled$/,
    /^\s*The operation was aborted.\s*$/,
  ].some(pattern => messages.some(m => {
    if (typeof pattern === 'string') return m.includes(pattern);
    return Boolean(m.match(pattern));
  }));
};

const notify = (obj) => {
  if (isBlackListed(obj)) {
    console.error('PagerDuty Addon error(not sent to bugsnag): ', obj);
  } else {
    Bugsnag.notify(obj);
  }
};

const moveUrlFromTitleToBody = ({ title, body }) => {
  let urlLocation = title.indexOf('http://');
  if (urlLocation === -1) urlLocation = title.indexOf('https://');

  if (urlLocation === -1) {
    return { title, body };
  }
  if (urlLocation === 0) {
    return { title: 'Error starts with URL:', body: `${title}. ${body}` };
  }
  return {
    title: title.substring(0, urlLocation),
    body: `${title.substring(urlLocation)}. ${body}`
  };
};

const replaceErrorWithUsefulMessage = ({ title, body }) => {
  if (title !== 'Error') return { title, body };
  let [newTitle, ...newBody] = body.split('.');
  return {
    title: newTitle,
    body: newBody.join('.')
  };
};

export const BugsnagErrorBoundary = Bugsnag.getPlugin('react');
export const bugsnagNotify = (error) => {
  if (Array.isArray(error)) {
    const throwable = error.filter(e => e.stack);
    if (throwable.length !== 0) {
      throwable.forEach(e => notify(e));
      return;
    }
  }

  if (error.stack) {
    notify(error);
    return;
  }

  const { title, body } = moveUrlFromTitleToBody(replaceErrorWithUsefulMessage(errorsToObject(error)));
  notify({ name: title, message: body });
};

export const setPagerDutyAccountMetadataForBugsnag = (meta) => {
  currentPagerDutyAccount = meta;
};
export const setJiraMetadataForBugsnag = (meta) => {
  jiraMetadata = meta;
};
